import { DocumentNode, gql } from '@apollo/client';
import {
  Button,
  Center,
  Grid,
  GridItem,
  Image,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import NextLink from 'next/link';
import * as React from 'react';

import { DeprecatedDialInInfo } from '@/components/DialInInfo';
import { EventAudience } from '@/components/EventAudience';
import { EventTime } from '@/components/EventTime';
import { VenueFragment } from '@/fragments/venue-fragment';
import { useCurrent } from '@/hooks/useCurrent';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { SpeakerCard } from '@/imports/ui/components/SpeakerCard';
import { ResourceCard } from '@/imports/ui/pages/conference/pages/conference-video-pages/components/SharedResourcesCard/components/ResourceCard';

import { ConferenceBoothMobileInfo_BoothFragment } from './graphql-types/ConferenceBoothMobileInfo_BoothFragment';

const INCREMENT_NUM = 3;

export const ConferenceBoothMobileInfo: React.FC<{
  conferenceBoothData?: ConferenceBoothMobileInfo_BoothFragment;
}> & { boothFragment: DocumentNode } = ({ conferenceBoothData }) => {
  const current = useCurrent();

  const moderatorIds = !!conferenceBoothData?.moderators
    ? conferenceBoothData.moderators.map((moderator) => moderator.user._id)
    : [];
  const currentUserIsModerator =
    !!current.user && moderatorIds.includes(current.user._id);

  const speakers = conferenceBoothData?.moderators ?? null;

  const [numfilesToShow, setNumfilesToShow] = React.useState(INCREMENT_NUM);

  if (!conferenceBoothData) {
    return null;
  }

  const allResources = conferenceBoothData.resources.results ?? null;
  const resourcesCount = allResources?.length ?? 0;

  const dialInProps =
    conferenceBoothData.venue.online &&
    conferenceBoothData.venue.online.__typename ===
      'EventVenue_TradewingPlatform' &&
    conferenceBoothData.venue.online.stream.__typename ===
      'EventVenue_TradewingPlatform_Stream_Live' &&
    conferenceBoothData.venue.online.stream.virtualEvent
      ? {
          dialInCode:
            conferenceBoothData.venue.online.stream.virtualEvent.dialInCode,
          listenerDialInCode:
            conferenceBoothData.venue.online.stream.virtualEvent
              .listenerDialInCode,
          dialInEnabled:
            conferenceBoothData.venue.online.stream.virtualEvent.dialInEnabled,
          dialInPhoneNumber:
            conferenceBoothData.venue.online.stream.virtualEvent
              .dialInPhoneNumber,
          isCurrentUserSpeaker: currentUserIsModerator,
          isParticipationAllowed:
            conferenceBoothData.venue.online.stream.virtualEvent
              .maxParticipants > 0,
        }
      : null;

  return (
    <VStack
      overflowY="scroll"
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
          display: 'none',
        },
      }}
    >
      <VStack shadow="md" spacing="1" w="full">
        <Image
          w="full"
          h="auto"
          zIndex="overlay"
          src={conferenceBoothData.image?.imageProxy || ''}
          alt="event banner"
          fallback={<ImageFallback w="full" aspectRatio="8/3" bg="gray.200" />}
        />

        <Stack w="full" p="4">
          <Text>
            <EventTime
              startDate={conferenceBoothData.parentEvent.startDate}
              endDate={conferenceBoothData.parentEvent.endDate}
            />
          </Text>

          <EventAudience event={conferenceBoothData.parentEvent} />
        </Stack>
      </VStack>

      {speakers?.length && (
        <VStack alignItems="flex-start" p="4" w="full" shadow="md" spacing="2">
          <Text fontWeight="semibold">
            {speakers.length > 1 ? 'Speakers' : 'Speaker'}
          </Text>

          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap="4">
            {speakers
              .filter((speaker) => !speaker.isHidden)
              .map((speaker) => (
                <NextLink
                  href={`/members/${speaker.user._id}`}
                  key={speaker.user._id}
                >
                  <GridItem>
                    <SpeakerCard user={speaker.user} />
                  </GridItem>
                </NextLink>
              ))}
          </Grid>
        </VStack>
      )}

      {conferenceBoothData.description !== null && (
        <VStack alignItems="flex-start" p="4" w="full" shadow="md">
          <Text fontWeight="semibold">Description</Text>
          <Text noOfLines={5} color="gray.600" textStyle="compact">
            {conferenceBoothData.description}
          </Text>
        </VStack>
      )}

      {conferenceBoothData.venue.online &&
        conferenceBoothData.venue.online.__typename ===
          'EventVenue_TradewingPlatform' &&
        conferenceBoothData.venue.online.stream.__typename ===
          'EventVenue_TradewingPlatform_Stream_Live' &&
        conferenceBoothData.venue.online.stream.virtualEvent &&
        conferenceBoothData.venue.online.stream.virtualEvent.dialInEnabled &&
        dayjs().isSameOrBefore(conferenceBoothData.parentEvent.endDate) && (
          <VStack
            spacing="4"
            w="full"
            justifyContent="flex-start"
            alignItems="flex-start"
            shadow="md"
            p="4"
          >
            <DeprecatedDialInInfo {...dialInProps} />
          </VStack>
        )}

      {resourcesCount > 0 && (
        <VStack
          spacing="4"
          w="full"
          justifyContent="flex-start"
          alignItems="flex-start"
          p="4"
        >
          <Text fontWeight="semibold">Resources</Text>
          {allResources?.slice(0, numfilesToShow).map((resource) => {
            return (
              <ResourceCard
                key={resource._id}
                resource={resource}
                canUserEdit={false}
                onRemoveResource={null}
                isRemoving={false}
              />
            );
          })}

          {numfilesToShow < resourcesCount && (
            <Center py="4" w="full">
              <Button
                variant="link"
                colorScheme="black"
                onClick={() =>
                  setNumfilesToShow(
                    (numfilesToShow) => numfilesToShow + INCREMENT_NUM
                  )
                }
              >
                See More Documents
              </Button>
            </Center>
          )}
        </VStack>
      )}
    </VStack>
  );
};

ConferenceBoothMobileInfo.boothFragment = gql`
  fragment ConferenceBoothMobileInfo_BoothFragment on ConferenceBooth {
    _id
    description
    image {
      _id
      imageProxy(input: { fit: "crop", ar: "8:3", w: 400, fm: "png" })
    }
    moderators {
      user {
        _id
        ...SpeakerCard_UserFragment
      }
      isHidden
    }
    venue {
      ...VenueFragment
    }
    parentEvent {
      _id
      startDate
      endDate
      ...EventAudience_EventFragment
    }
    canCurrentUserEditResources
    resources(input: { offset: 0, limit: 100 }) {
      results {
        _id
        authorId
        dateCreated
        resourceSpec {
          ... on EventResource_Spec_Document {
            file {
              name
              downloadUrl
              contentType
            }
          }
        }
      }
      totalCount
    }
  }
  ${EventAudience.eventFragment}
  ${SpeakerCard.userFragment}
  ${VenueFragment}
`;
